


























import { PropType } from 'vue';
import { ErrorManager } from '@/models/error';
import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';
import { Invoice, IInvoiceData, IInvoice } from '@/models';
import { getPropertyFromItem } from '@/utils/helpers';
import { IFetchInvoicesParams, IInvoicePaginatedListData } from '@/services';

export default mixins(Functions).extend({
  name: 'InvoicePicker',

  model: {
    prop: 'value',
    event: 'input'
  },

  props: {
    value: [Number, Object],
    externalErrorMessage: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    filter: {
      type: Object as PropType<IFetchInvoicesParams>
    }
  },

  data() {
    return {
      lazyValue: this.value,
      search: '',
      errorMessage: '',
      selectedInvoice: null,
      localInvoicesList: [] as IInvoiceData[],
      page: 1,
      loading: false
    };
  },

  watch: {
    value: {
      handler(val: any, prevVal: any) {
        this.lazyValue = val;
        if (val !== prevVal) {
          // search for the correct invoice
          const propertyValue = getPropertyFromItem(val, 'number', val);

          const searchQuery =
            typeof propertyValue == 'number'
              ? String(propertyValue)
              : propertyValue;

          this.debounceFetchInvoices(searchQuery ?? '');
        }
      },
      immediate: true
    }
  },

  computed: {
    internalValue: {
      get(): any {
        return this.lazyValue;
      },
      set(val: any) {
        this.lazyValue = val;
        this.$emit('input', val);
      }
    },

    customAttrs(): object {
      return this.$attrs;
    },

    errorMessageList(): string[] {
      let errorList: string[] = [];
      if (this.externalErrorMessage) {
        errorList = this.externalErrorMessage;
      }
      if (this.errorMessage) {
        errorList.push(this.errorMessage);
      }

      return errorList;
    }
  },
  mounted() {
    this.debounceFetchInvoices();
  },

  methods: {
    clearAllErrors(): void {
      this.errorMessage = '';
    },

    debounceFetchInvoices(search?: string) {
      this.clearAllErrors();
      // prevent fetching if search is an object or if search is the same as the previous search
      if (typeof search == 'object' || this.search == search) return;

      this.loading = true;
      let params: IFetchInvoicesParams = {
        page: 1,
        search: search
      };

      if (this.filter) {
        params = {
          ...params,
          ...this.filter
        };
      }

      this.debounce(this.searchInvoices, 1000)(params);
    },

    searchInvoices(params: IFetchInvoicesParams) {
      this.loading = true;
      return this.$services.invoice
        .fetchInvoices(params)
        .then((data: IInvoicePaginatedListData) => {
          // set the search value to the search query
          this.search = params.search ?? '';

          this.localInvoicesList = data.results.map(
            (invoice: IInvoiceData) => new Invoice(invoice)
          );
          if (this.internalValue) {
            const currentInvoice = this.localInvoicesList.find(
              invoice => invoice.id == this.getInvoiceValue(this.internalValue)
            );

            // Add current invoice to list if not in initial list
            if (!currentInvoice) {
              this.$services.invoice
                .fetchInvoice(this.getInvoiceValue(this.internalValue))
                .then((data: IInvoiceData) => {
                  this.localInvoicesList.unshift(new Invoice(data));
                });
            }
          }
        })
        .catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * removing filter from the invoice search as to get the raw data from the server as is
     */
    itemSearch(item: any, queryText: string, itemText: string) {
      return true;
    },

    getInvoiceValue(invoice: IInvoiceData) {
      return getPropertyFromItem(invoice, 'id', invoice);
    },

    stringRepresentation(invoice: IInvoice) {
      return invoice.toCustomerInvoiceString();
    }
  }
});
